if (!customElements.get('product-form')) {
  class ProductForm extends HTMLElement {
    constructor() {
      super();
      if (this.hasChildNodes()) this.init();
    }

    init() {
      this.form = this.querySelector('.js-product-form');
      this.form.querySelector('[name="id"]').disabled = false;
      this.cartDrawer = document.querySelector('cart-drawer');
      this.submitBtn = this.querySelector('[name="add"]');
      this.sectionId = this.dataset.sectionId || null;

      if (theme.settings.afterAtc !== 'no-js') {
        this.form.addEventListener('submit', this.handleSubmit.bind(this));
      }

      // Cart mutations that don't go through this form's submit — e.g. the
      // cart drawer's own 2-voor-upsell "add this colour" tile (dispatches
      // dispatch:cart-drawer:refresh) or its qty stepper +/- (dispatches
      // on:line-item:change via cart-items.js) — signal via these events
      // instead, so re-check the deal progress bar whenever they fire.
      if (this.sectionId) {
        const refreshDealProgress = this.refreshDealProgress.bind(this);
        document.addEventListener('dispatch:cart-drawer:refresh', refreshDealProgress);
        document.addEventListener('on:line-item:change', refreshDealProgress);
        this.setupDealProgressObserver();
      }
    }

    /**
     * Handles submission of the product form.
     * @param {object} evt - Event object.
     */
    async handleSubmit(evt) {
      if (window.location.pathname === theme.routes.cart) return;

      evt.preventDefault();

      if (this.submitBtn.getAttribute('aria-disabled') === 'true') return;

      this.errorMsg = null;
      this.setErrorMsgState();

      // Disable "Add to Cart" button until submission is complete.
      this.submitBtn.setAttribute('aria-disabled', 'true');
      this.submitBtn.classList.add('is-loading');
      this.submitBtn.setAttribute('disabled', 'disabled');

      const formData = new FormData(this.form);
      const sectionIds = this.cartDrawer ? [this.cartDrawer.dataset.section, 'cart-icon-bubble'] : ['cart-icon-bubble'];
      if (this.sectionId) sectionIds.push(this.sectionId);
      const sections = sectionIds.join(',');

      formData.append('sections_url', window.location.pathname);
      formData.append('sections', sections);

      const fetchRequestOpts = {
        method: 'POST',
        headers: {
          Accept: 'application/javascript',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: formData
      };

      try {
        const oldCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!oldCartResponse.ok) throw new Error(oldCartResponse.status);
        const oldCartData = await oldCartResponse.json();

        const response = await fetch(theme.routes.cartAdd, fetchRequestOpts);
        const data = await response.json();
        const error = typeof data.description === 'string' ? data.description : data.message;

        if (data.status) this.setErrorMsgState(error);

        if (!response.ok) throw new Error(response.status);

        if (theme.settings.afterAtc === 'page') {
          // Allow the tick animation to complete
          setTimeout(() => {
            window.location.href = theme.routes.cart;
          }, 300);
        } else {
          // Update cart icon count.
          ProductForm.updateCartIcon(data);

          // Refresh the 2-voor-deal progress bar (if this PDP has one).
          if (this.sectionId && data.sections && data.sections[this.sectionId]) {
            this.updateDealProgress(data.sections[this.sectionId]);
          }

          // Bundle apps (e.g. BundleSuite) can merge line items into a bundle
          // product asynchronously, after this response already landed — the
          // update above may still be looking at the pre-merge cart. Re-check
          // once things have settled, same delay cart-2voor-upsell.js assumes.
          if (this.sectionId) {
            setTimeout(() => this.refreshDealProgress(), 1500);
          }

          // If item was added from Quick Add drawer, show "Added to cart" message.
          const quickAddDrawer = this.closest('quick-add-drawer');
          if (quickAddDrawer) quickAddDrawer.addedToCart();

          setTimeout(() => {
            // Update cart drawer contents.
            if (this.cartDrawer) {
              this.cartDrawer.renderContents(
                data,
                !quickAddDrawer && theme.settings.afterAtc === 'drawer'
              );
            } else if (window.location.pathname === theme.routes.cart) {
              const cartItems = document.querySelector('cart-items');
              if (cartItems) {
                if (cartItems.dataset.empty === 'true') {
                  window.location.reload();
                } else {
                  cartItems.refresh();
                }
              }
            }
          }, 700);
        }

        const newCartResponse = await fetch(`${theme.routes.cart}.js`);
        if (!newCartResponse.ok) throw new Error(newCartResponse.status);
        const newCartData = await newCartResponse.json();
        const itemInOldCart = oldCartData.items.filter(
          (item) => item.variant_id === data.variant_id
        )[0];

        // Check if product was already in the cart
        if (itemInOldCart) {
          this.dispatchEvent(new CustomEvent('on:line-item:change', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id,
              oldQuantity: itemInOldCart.quantity,
              newQuantity: (itemInOldCart.quantity === data.quantity)
                ? itemInOldCart.quantity : data.quantity
            }
          }));
        } else {
          this.dispatchEvent(new CustomEvent('on:cart:add', {
            bubbles: true,
            detail: {
              cart: newCartData,
              variantId: data.variant_id
            }
          }));
        }

        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.add('is-success');
        this.submitBtn.removeAttribute('aria-disabled');
        setTimeout(() => {
          this.submitBtn.classList.remove('is-loading');
          this.submitBtn.classList.remove('is-success');
          this.submitBtn.removeAttribute('disabled');
        }, 2000);
      } catch (error) {
        console.log(error); // eslint-disable-line
        this.dispatchEvent(new CustomEvent('on:cart:error', {
          bubbles: true,
          detail: {
            error: this.errorMsg.textContent
          }
        }));

        // Re-enable 'Add to Cart' button.
        this.submitBtn.classList.remove('is-loading');
        this.submitBtn.classList.remove('is-success');
        this.submitBtn.removeAttribute('disabled');
        this.submitBtn.removeAttribute('aria-disabled');

        // Reload cart drawer
        if (this.cartDrawer) this.cartDrawer.refresh(true);
      }
    }

    /**
     * Updates the cart icon count in the header.
     * @param {object} response - Response JSON.
     */
    static updateCartIcon(response) {
      const cartIconBubble = document.getElementById('cart-icon-bubble');
      cartIconBubble.innerHTML = response.sections['cart-icon-bubble'];
    }

    /**
     * Swaps in the freshly server-rendered 2-voor-deal progress bar so it
     * reflects the new cart state without a page reload.
     * @param {string} sectionHtml - Re-rendered HTML of this product-form's section.
     */
    updateDealProgress(sectionHtml) {
      const current = this.querySelector('.pdp-2voor-progress');
      if (!current) return;

      const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
      const updated = doc.querySelector('.pdp-2voor-progress');

      if (updated) {
        current.outerHTML = updated.outerHTML;
      } else {
        current.remove();
      }
    }

    /**
     * Some cart mutations (e.g. the qty stepper's +/- when a bundle app
     * intercepts the click before Shopify's normal quantity-change flow
     * completes) never fire on:line-item:change or dispatch:cart-drawer:refresh
     * at all — cart-2voor-upsell.js works around the same problem for the
     * drawer with a MutationObserver, so mirror that here as a catch-all.
     */
    setupDealProgressObserver() {
      // No document.body fallback here on purpose: this.updateDealProgress()
      // mutates .pdp-2voor-progress, which lives outside cart-drawer/cart-items
      // — but IS inside body, so observing body would re-trigger on our own
      // swap and loop forever.
      const target = document.querySelector('cart-drawer') || document.getElementById('cart-items');
      if (!target) return;

      let timer = null;
      const observer = new MutationObserver(() => {
        clearTimeout(timer);
        timer = setTimeout(() => this.refreshDealProgress(), 400);
      });
      observer.observe(target, { childList: true, subtree: true });
    }

    /**
     * Re-fetches this section fresh from the server and re-applies the
     * 2-voor-deal progress bar, to catch cart state that settled after an
     * async bundle-app merge (see the setTimeout call-site in handleSubmit).
     */
    refreshDealProgress() {
      if (!this.sectionId || !this.querySelector('.pdp-2voor-progress')) return;

      fetch(`${window.location.pathname}?section_id=${this.sectionId}`)
        .then((res) => (res.ok ? res.text() : Promise.reject(res.status)))
        .then((html) => this.updateDealProgress(html))
        .catch((err) => console.error('[2voor] PDP deal-progress refresh failed:', err));
    }

    /**
     * Shows/hides an error message.
     * @param {string} [error=false] - Error to show a message for.
     */
    setErrorMsgState(error = false) {
      this.errorMsg = this.errorMsg || this.querySelector('.js-form-error');
      if (!this.errorMsg) return;

      this.errorMsg.hidden = !error;
      if (error) this.errorMsg.textContent = error;
    }
  }

  customElements.define('product-form', ProductForm);
}
